import { hasCapability } from "@/lib/roles";

export const money = (minor) => `₦${((Number(minor) || 0) / 100).toLocaleString("en-NG", { maximumFractionDigits: 2 })}`;

export const EVENT_LABELS = {
  OBLIGATION_CHARGED: "School fee charged", OBLIGATION_REVERSED: "Charge cancelled",
  SCHOLARSHIP_APPLIED: "Scholarship applied",
  SCHOLARSHIP_REVERSED: "Scholarship reversed", PAYMENT_RECEIVED: "Payment received",
  PAYMENT_ALLOCATED: "Payment allocated", PAYMENT_ALLOCATION_REVERSED: "Payment allocation reversed",
  PAYMENT_REFUNDED: "Payment refunded",
};

const rolesOf = (user) => new Set(user?.roles || (user?.role ? [user.role] : []));
const paymentRoles = new Set(["super_admin", "director", "accountant", "receptionist"]);

export const parseMinor = (value) => {
  const clean = String(value || "").replace(/,/g, "").trim();
  if (!/^\d+(\.\d{1,2})?$/.test(clean)) return null;
  const [whole, fraction = ""] = clean.split(".");
  const minor = Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
  return Number.isSafeInteger(minor) && minor > 0 ? minor : null;
};

export const familyPlacementDetails = (data, studentId) => (data?.placements || [])
  .filter((item) => item.student_id === studentId)
  .map((item) => ({
    placement: item,
    detail: {
      statement: {
        total_charged_minor: item.charged_minor,
        total_scholarship_minor: item.scholarship_minor,
        total_allocated_minor: item.paid_minor,
        available_credit_minor: item.available_credit_minor,
        outstanding_balance_minor: item.outstanding_minor,
        reconciliation: { status: item.reconciliation_status },
      },
      invoices: (item.invoices || []).map((invoice) => ({ ...invoice, id: invoice.invoice_id, net_balance_minor: invoice.outstanding_minor })),
    },
  }));

export const canRecordFinancePayment = (user) => [...rolesOf(user)].some((role) => paymentRoles.has(role));
export const canManageScholarships = (user) => rolesOf(user).has("super_admin") || rolesOf(user).has("director");
// UX only; the backend enforces capability and scope. Each action has its own
// capability: finalizing a payment reversal never implies cancelling an
// invoice, and vice versa.
export const canFinalizeFinanceReversal = (user) => hasCapability(user, "finance.reversal.finalize");
export const canCancelFinanceInvoice = (user) => hasCapability(user, "finance.invoice.cancel");

// Which Finance mutation controls a panel may render (UX only; the backend
// enforces capability and scope). Legacy callers keep their existing role
// gates. The finance-owned account page additionally requires the capability
// each action's endpoint demands, so finance.account.view alone renders none.
export const financeMutationControls = (user, { parentMode = false, capabilityGated = false } = {}) => {
  if (parentMode) return { recordPayment: false, recordAdvance: false, applyCredit: false, requestRefund: false, requestReversal: false, manageScholarships: false, cancelCharge: false };
  const record = canRecordFinancePayment(user);
  const scholarship = canManageScholarships(user);
  const cancelCharge = canCancelFinanceInvoice(user);
  if (!capabilityGated) return { recordPayment: record, recordAdvance: record, applyCredit: record, requestRefund: record, requestReversal: record, manageScholarships: scholarship, cancelCharge };
  const can = (capability) => hasCapability(user, capability);
  return {
    recordPayment: record && can("finance.payment.record") && can("finance.payment.allocate"),
    recordAdvance: record && can("finance.payment.record"),
    applyCredit: record && can("finance.payment.allocate"),
    requestRefund: record && can("finance.refund.request"),
    requestReversal: record && can("finance.reversal.request"),
    manageScholarships: scholarship && can("finance.scholarship.manage"),
    cancelCharge,
  };
};

export const FINANCE_ACCOUNT_IDENTITY = ["enrollment_id", "program_id", "session_id", "term_id"];

// Reload- and bookmark-safe link to the finance-owned placement account page.
export const financeAccountPath = (row) => {
  const params = new URLSearchParams(FINANCE_ACCOUNT_IDENTITY.map((key) => [key, row?.[key] || ""]));
  return `/app/fees/accounts/${encodeURIComponent(row?.student_id || "")}?${params.toString()}`;
};

// The exact placement identity from the route, or null when any part is
// missing: the page never guesses a placement or period.
export const financeAccountFromRoute = (studentId, searchParams) => {
  const account = Object.fromEntries(FINANCE_ACCOUNT_IDENTITY.map((key) => [key, searchParams.get(key) || ""]));
  return studentId && FINANCE_ACCOUNT_IDENTITY.every((key) => account[key]) ? { student_id: studentId, ...account } : null;
};

export const bankAllocationPreview = (receivedMinor, outstandingMinor = 0) => {
  const received = Math.max(0, Number(receivedMinor || 0));
  const outstanding = Math.max(0, Number(outstandingMinor || 0));
  const allocated = Math.min(received, outstanding);
  return { receivedMinor: received, allocatedMinor: allocated, availableCreditMinor: received - allocated };
};

export const explicitAllocationPlan = (invoices = [], allocationAmounts = {}) => {
  const allocations = invoices.map((invoice) => ({ invoice, amountMinor: parseMinor(allocationAmounts[invoice.id]) || 0 })).filter((item) => item.amountMinor > 0);
  return { allocations, totalMinor: allocations.reduce((sum, item) => sum + item.amountMinor, 0), exceedsOutstanding: allocations.some(({ invoice, amountMinor }) => amountMinor > Number(invoice.net_balance_minor || 0)) };
};
